import { Car, BatteryMedium, Wrench, Zap } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchMaintenanceSummary } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";
import { VehicleImageTrigger, MaintenancePopup } from "@/components/VehicleMaintenanceInfo";

const tmin = (t) => {
  if (!t) return 0;
  const [h, m] = t.slice(0, 5).split(":").map(Number);
  return h * 60 + m;
};
const fmt = (t) => (t ? t.slice(0, 5) : "");
const fmtMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const meta = (v) => {
  return {
    img: v.image_url || null,
    badge: v.requires_licence ? "EV" : `${v.capacity} Seater`,
  };
};

const LEGEND = [
  { label: "Available", cls: "bg-[#D8EFE1] border-[#AEDCC1]" },
  { label: "Booked / Reserved", cls: "bg-[#E4DCF5] border-[#C4B2E8]" },
  { label: "In Use", cls: "bg-[#DBEAFE] border-[#93C5FD]" },
  { label: "Overdue", cls: "bg-[#F9D2D2] border-[#EBA9A9]" },
  { label: "Under Maintenance", cls: "bg-slate-200 border-slate-400" },
  { label: "Completed / Returned", cls: "bg-[#FEF9C3] border-[#FDE047]" },
  { label: "Handover (15 min)", cls: "bg-[#FBE7C6] border-[#F0CE93]" },
];

const DAY = 24 * 60;
const pL = (m) => `${(m / DAY) * 100}%`;
const pW = (dur) => `${(Math.max(0, dur) / DAY) * 100}%`;

// Responsive label cadence: mobile every 6h, tablet/desktop every 3h (range always full 24h).
const labelVis = (h) => {
  if (h % 3 === 0) return "";
  return "hidden";
};

export const AvailabilityTimeline = ({ availability, selectedDate }) => {
  const hours = [];
  for (let h = 0; h <= 24; h++) hours.push(h);

  const isToday = selectedDate === todayStr();
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const showNow = isToday;

  const statusText = (v) => {
    const slots = [...v.booked_slots].sort((a, b) => tmin(a.start_time) - tmin(b.start_time));
    if (isToday) {
      const idx = slots.findIndex((s) => nowMin >= tmin(s.start_time) && nowMin < tmin(s.buffer_end));
      if (idx >= 0) {
        let end = tmin(slots[idx].return_time);
        let bufEnd = tmin(slots[idx].buffer_end);
        for (let i = idx + 1; i < slots.length; i++) {
          if (tmin(slots[i].start_time) <= bufEnd) {
            end = tmin(slots[i].return_time);
            bufEnd = tmin(slots[i].buffer_end);
          } else break;
        }
        return `Available after ${fmtMin(end)}`;
      }
    }
    return "Available";
  };

  const blockCls = (status) => {
    switch (status) {
      case "In Use":
        return "border-[#93C5FD] bg-[#DBEAFE] text-[#1D4ED8]";
      case "Overdue":
        return "border-[#EBA9A9] bg-[#F9D2D2] text-[#B91C1C]";
      case "Returned":
        return "border-[#FDE047] bg-[#FEF9C3] text-[#854D0E]";
      default:
        return "border-[#C4B2E8] bg-[#E4DCF5] text-[#4A2475]";
    }
  };
  const statusTag = (s) => {
    if (s.status === "In Use") return " · In Use";
    if (s.status === "Overdue") return " · Overdue";
    if (s.status === "Returned") return " · Returned";
    return "";
  };

  const { data: maintSummary = [] } = useQuery({ queryKey: ["maintenance-summary"], queryFn: fetchMaintenanceSummary, staleTime: 60000 });
  const svcByVehicle = Object.fromEntries((maintSummary || []).map((s) => [s.vehicle, s]));
  const dotCls = { grey: "bg-slate-300", green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
  const [popupVehicle, setPopupVehicle] = useState(null);

  return (
    <div data-testid="availability-timeline">
      {/* Legend */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="timeline-legend">
        {LEGEND.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#475569]">
            <span className={`h-3 w-3 rounded-sm border ${l.cls}`} />
            {l.label}
          </span>
        ))}
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-slate-200" data-testid="timeline-scroll">
        <div className="min-w-[760px] lg:min-w-0">
        {/* Header */}
        <div className="flex border-b border-slate-200 bg-white">
          <div className="sticky left-0 z-40 flex w-32 shrink-0 items-center border-r border-slate-100 bg-white px-2 py-2 text-xs font-semibold text-[#0F172A] sm:w-52 sm:px-4 sm:text-sm">
            Time
          </div>
          <div className="relative min-w-0 flex-1" style={{ height: 40 }}>
            {hours.map((h) => (
              <span
                key={h}
                className={`absolute top-1/2 whitespace-nowrap text-[10px] font-medium text-[#64748B] sm:text-xs ${labelVis(h)}`}
                style={{
                  left: pL(h * 60),
                  transform: `translateY(-50%) translateX(${h === 0 ? "0" : h === 24 ? "-100%" : "-50%"})`,
                }}
              >
                {h === 24 ? "23:59" : `${String(h).padStart(2, "0")}:00`}
              </span>
            ))}
            {showNow && (
              <div className="absolute -top-1 z-30 -translate-x-1/2" style={{ left: pL(nowMin) }}>
                <span className="rounded-md bg-[#5C2D91] px-1.5 py-0.5 text-[10px] font-bold text-white shadow" data-testid="timeline-now">
                  NOW {fmtMin(nowMin)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Rows */}
        {availability.map((v) => {
          const m = meta(v);
          const ms = svcByVehicle[v.vehicle];
          const maintNow = isToday && (v.maintenance_slots || []).find((mm) => nowMin >= tmin(mm.start_time) && nowMin < tmin(mm.end_time));
          const MaintIcon = maintNow ? (maintNow.block_type === "Charging" ? Zap : Wrench) : null;
          return (
            <div key={v.vehicle} className="flex border-b border-slate-100 last:border-0" data-testid={`timeline-row-${v.vehicle}`}>
              {/* Left panel */}
              <div className="sticky left-0 z-40 flex w-32 shrink-0 items-center gap-2 border-r border-slate-100 bg-white px-2 py-2 shadow-[4px_0_6px_-4px_rgba(15,23,42,0.12)] sm:w-52 sm:gap-3 sm:px-3 lg:shadow-none">
                <VehicleImageTrigger ms={ms} img={m.img} onOpen={() => setPopupVehicle(v.vehicle)}>
                  <div className="flex h-10 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-50">
                    {m.img ? (
                      <img src={m.img} alt={v.vehicle} className="h-full w-full object-contain" />
                    ) : (
                      <Car className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </VehicleImageTrigger>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    {ms ? (
                      <button type="button" onClick={() => setPopupVehicle(v.vehicle)} className="break-words text-left text-[11px] font-bold leading-tight text-[#0F172A] underline-offset-2 hover:text-[#5C2D91] hover:underline sm:text-sm" data-testid={`vehicle-name-btn-${v.vehicle}`}>{v.vehicle}</button>
                    ) : (
                      <p className="break-words text-[11px] font-bold leading-tight text-[#0F172A] sm:text-sm">{v.vehicle}</p>
                    )}
                    {MaintIcon && (
                      <MaintIcon className="h-3.5 w-3.5 shrink-0 text-slate-500" title={`Under ${maintNow.block_type}`} data-testid={`maint-active-${v.vehicle}`} />
                    )}
                  </div>
                  <span className="mt-0.5 inline-block rounded bg-[#EEF2FF] px-1.5 py-0.5 text-[9px] font-semibold text-[#4338CA] sm:text-[10px]">
                    {m.badge}
                  </span>
                  {ms && (
                    <span
                      title={`${v.vehicle}\nMaintenance: ${ms.status}${ms.next_maintenance_date ? `\nNext service: ${ms.next_maintenance_date}` : ""}${ms.road_tax ? `\nRoad tax: ${ms.road_tax.label}` : ""}`}
                      className="mt-0.5 flex items-center gap-1 text-[9px] font-semibold sm:text-[10px]"
                      data-testid={`maint-badge-${v.vehicle}`}
                    >
                      <span className={`h-2 w-2 shrink-0 rounded-full ${dotCls[ms.level] || "bg-slate-300"}`} />
                      <span className="break-words leading-tight text-[#475569]">{ms.has_record ? ms.status : "No Maintenance Record"}</span>
                    </span>
                  )}
                  {ms && ms.is_proton && ms.road_tax && (
                    <span
                      title={`Road tax: ${ms.road_tax.label}`}
                      className={`mt-0.5 inline-block max-w-full truncate whitespace-nowrap rounded px-1.5 py-0.5 text-[9px] font-semibold sm:text-[10px] ${ms.road_tax.level === "red" ? "bg-red-50 text-red-700" : ms.road_tax.level === "amber" ? "bg-amber-50 text-amber-700" : ms.road_tax.level === "green" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                      data-testid={`roadtax-badge-${v.vehicle}`}
                    >
                      Road Tax: {ms.road_tax.label.replace(/ Days? Left/, "d left").replace(/ Days? Ago/, "d ago")}
                    </span>
                  )}
                  <p className="mt-0.5 flex items-start gap-1 text-[10px] font-medium text-emerald-600 sm:text-[11px]">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span className="break-words leading-tight">{statusText(v)}</span>
                  </p>
                  <p
                    className={`mt-0.5 flex items-start gap-1 text-[10px] font-medium sm:text-[11px] ${
                      v.latest_battery_after == null
                        ? "text-slate-400"
                        : v.latest_battery_after >= 60
                          ? "text-emerald-600"
                          : v.latest_battery_after >= 30
                            ? "text-amber-600"
                            : "text-red-600"
                    }`}
                    data-testid={`vehicle-battery-${v.vehicle}`}
                  >
                    <BatteryMedium className="mt-0.5 h-3 w-3 shrink-0" />
                    <span className="break-words leading-tight">{v.latest_battery_after == null ? "Battery not updated" : `${v.latest_battery_after}% battery`}</span>
                  </p>
                </div>
              </div>

              {/* Track */}
              <div className="relative min-w-0 flex-1 bg-[#EAF6EF]" style={{ height: 88 }}>
                {/* hour gridlines every 3h */}
                {hours.filter((h) => h > 0 && h < 24 && h % 3 === 0).map((h) => (
                  <div key={h} className="absolute top-0 bottom-0 w-px bg-emerald-100" style={{ left: pL(h * 60) }} />
                ))}

                {v.booked_slots.length === 0 && (v.maintenance_slots || []).length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-emerald-700 sm:text-sm">
                    Available throughout
                  </div>
                )}

                {(v.maintenance_slots || []).map((mm) => {
                  const MIcon = mm.block_type === "Charging" ? Zap : Wrench;
                  return (
                    <div
                      key={mm.id}
                      className="absolute top-1 bottom-1 flex flex-col justify-center overflow-hidden rounded-md border border-slate-400 bg-slate-200 px-1 py-0.5 text-[9px] leading-tight text-slate-700"
                      style={{ left: pL(tmin(mm.start_time)), width: pW(tmin(mm.end_time) - tmin(mm.start_time)), minWidth: 3 }}
                      title={`${mm.block_type} ${fmt(mm.start_time)}–${fmt(mm.end_time)}`}
                      data-testid={`timeline-maint-${mm.id}`}
                    >
                      <p className="flex items-center gap-1 truncate font-bold"><MIcon className="h-3 w-3 shrink-0" /> {mm.block_type}</p>
                      <p className="truncate">{fmt(mm.start_time)}–{fmt(mm.end_time)}</p>
                    </div>
                  );
                })}

                {v.booked_slots.map((s) => {
                  const bLeft = pL(tmin(s.start_time));
                  const bWidth = pW(tmin(s.return_time) - tmin(s.start_time));
                  const hLeft = pL(tmin(s.return_time));
                  const hWidth = pW(tmin(s.buffer_end) - tmin(s.return_time));
                  const showHandover = tmin(s.buffer_end) - tmin(s.return_time) > 0 && s.status !== "Overdue";
                  return (
                    <div key={s.booking_ref}>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className={`absolute top-1 bottom-1 cursor-pointer overflow-hidden rounded-md border px-1.5 py-0.5 text-left text-[10px] leading-tight outline-none transition-shadow hover:ring-2 hover:ring-[#5C2D91]/40 focus:ring-2 focus:ring-[#5C2D91] ${blockCls(s.status)}`}
                            style={{ left: bLeft, width: bWidth, minWidth: 3 }}
                            data-testid={`timeline-booked-${s.booking_ref}`}
                          >
                            <p className="truncate font-bold">
                              {s.continues_prev_day && "← "}{fmt(s.start_time)} – {fmt(s.return_time)}{s.continues_next_day && " →"}{statusTag(s)}
                            </p>
                            <p className="truncate text-[9px] opacity-80">PNR: {s.booking_ref}</p>
                            <p className="truncate">{s.employee_name}</p>
                            {s.status === "Returned" && s.battery != null && (
                              <p className="truncate flex items-center gap-1 font-semibold" data-testid={`timeline-battery-${s.booking_ref}`}>
                                <BatteryMedium className="h-3 w-3 shrink-0" /> {s.battery}%
                              </p>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          className="w-64 rounded-xl border-slate-200 p-4 shadow-lg"
                          data-testid={`timeline-detail-${s.booking_ref}`}
                        >
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="truncate text-sm font-bold text-[#0F172A]">{s.employee_name}</p>
                            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${blockCls(s.status)}`}>
                              {s.status}
                            </span>
                          </div>
                          <dl className="space-y-1.5 text-xs text-[#475569]">
                            <div className="flex justify-between gap-2">
                              <dt className="font-medium">Vehicle</dt>
                              <dd className="truncate text-right text-[#0F172A]">{v.vehicle}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="font-medium">PNR</dt>
                              <dd className="font-mono text-[#5C2D91]">{s.booking_ref}</dd>
                            </div>
                            <div className="flex justify-between gap-2">
                              <dt className="font-medium">Booked</dt>
                              <dd className="text-[#0F172A]">{fmt(s.start_time)} – {fmt(s.return_time)}</dd>
                            </div>
                            {(s.continues_next_day || s.continues_prev_day) && (
                              <div className="flex justify-between gap-2" data-testid={`timeline-spans-${s.booking_ref}`}>
                                <dt className="font-medium">Spans</dt>
                                <dd className="text-right font-semibold text-[#5C2D91]">
                                  {s.continues_prev_day ? "← Continued from previous day" : "Continues next day →"}
                                </dd>
                              </div>
                            )}
                            {fmt(s.estimated_return_time) !== fmt(s.return_time) && (
                              <div className="flex justify-between gap-2">
                                <dt className="font-medium">Est. return</dt>
                                <dd className="text-[#0F172A]">{fmt(s.estimated_return_time)}</dd>
                              </div>
                            )}
                            {showHandover && (
                              <div className="flex justify-between gap-2">
                                <dt className="font-medium">Handover</dt>
                                <dd className="text-[#B45309]">{fmt(s.return_time)} – {fmt(s.buffer_end)}</dd>
                              </div>
                            )}
                            <div className="flex justify-between gap-2">
                              <dt className="font-medium">Battery</dt>
                              <dd className={s.status === "Returned" && s.battery != null ? "font-semibold text-[#0F172A]" : "text-slate-400"}>
                                {s.status === "Returned" && s.battery != null ? `${s.battery}%` : "Not updated"}
                              </dd>
                            </div>
                          </dl>
                        </PopoverContent>
                      </Popover>
                      {showHandover && (
                        <div
                          className="absolute top-1 bottom-1 overflow-hidden rounded-md border border-[#F0CE93] bg-[#FBE7C6] px-1 py-0.5 text-[9px] leading-tight text-[#B45309]"
                          style={{ left: hLeft, width: hWidth, minWidth: 3 }}
                          title={`Handover ${fmt(s.return_time)}–${fmt(s.buffer_end)}`}
                          data-testid={`timeline-handover-${s.booking_ref}`}
                        >
                          <p className="truncate font-semibold">{fmt(s.return_time)}–{fmt(s.buffer_end)}</p>
                          <p className="truncate">Handover</p>
                        </div>
                      )}
                    </div>
                  );
                })}

                {showNow && (
                  <div className="absolute top-0 bottom-0 z-20 w-px bg-[#5C2D91]" style={{ left: pL(nowMin) }} />
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
      {popupVehicle && svcByVehicle[popupVehicle] && (
        <MaintenancePopup ms={svcByVehicle[popupVehicle]} img={availability.find((a) => a.vehicle === popupVehicle)?.image_url || null} open onOpenChange={(o) => !o && setPopupVehicle(null)} />
      )}
    </div>
  );
};
